import { isEqual } from 'lodash';
import type { MutableRefObject } from 'react';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@interstate/components/Button';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import type { ManualIncentiveEditEntry } from '@makemydeal/dr-dash-store/types/manualIncentiveTypes';
import { CASH } from '@makemydeal/dr-platform-types';
import { INCENTIVE_TYPE_VALUES, type StateTree } from '@makemydeal/dr-dash-types';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as programActionCreators from '@makemydeal/dr-dash-store/actions/programActionCreators';
import * as incentivesSelectors from '@makemydeal/dr-dash-store/selectors/incentivesSelectors';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import * as programsSelectors from '@makemydeal/dr-dash-store/selectors/programsSelectors';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import { ManualRebatesForm } from './ManualRebatesForm';
import { AddIncentiveContainer, RebatesEditWrapper } from './Rebates.style';
import {
    isIncentiveFromProgram,
    isProgramIncentiveOverride,
    createNewManualIncentiveEntry,
    isIncentiveFormValid,
    getRequiredFields,
    toComparableIncentives,
    generateEditId
} from '../../utils/rebatesUtils';
import { mergeIncentives, stampMissingEditIds } from '../../utils/rebatesEditUtils';
import { sortStandardIncentivesFirst } from '@makemydeal/dr-dash-store/utils/manualIncentivesUtils';
import { ADD_INCENTIVE_BUTTON, INCENTIVE_MODIFICATION_ACTION, INCENTIVE_NOT_EDITED } from '../../constants';
import type { IncentiveModificationAction } from '../../types';

const isDealerCashEntry = (incentive: ManualIncentiveEditEntry): boolean =>
    incentive.type === INCENTIVE_TYPE_VALUES.dealerCash.value;

type RebatesEditProps = {
    isIncentivesFormDirtyRef?: MutableRefObject<boolean>;
};

export const RebatesEdit: FC<RebatesEditProps> = ({ isIncentivesFormDirtyRef }) => {
    const dispatch = useDispatch();

    const appliedIncentivesWithEditIds = useSelector(incentivesSelectors.getAppliedIncentivesWithEditIds, isEqual);
    const term = useSelector(offerSelectors.getSelectedTermMonths);
    const hasValidationErrors = useSelector(incentivesSelectors.getHasIncentiveValidationErrors);
    const dealerCashTotal = useSelector(offerReduxSelectors.getAppliedIncentivesTotalForDealerCash);
    const nonDealerCashTotal = useSelector(offerReduxSelectors.getAppliedIncentivesTotalForNonDealerCash);
    const isOptionalDeskingMode = useSelector(dealModeSelectors.isOptionalDeskingMode);
    const isRestorationEnabled = useSelector(featureToggleSelectors.isProgramIncentivesRestorationEnabled);
    const currentProgramContextId = useSelector(
        (state: StateTree) => programsSelectors.getSelectedTermIncentivesContext(state)?.programContextId
    );
    const isApplying = useSelector(programsSelectors.isApplyingProgram);
    const storedManualIncentives = useSelector(offerReduxSelectors.getManualIncentives, isEqual);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isCashFixesEnabled = useSelector(featureToggleSelectors.isCashDealIncentivesFixesEnabled);
    const isKeepUnnamedManualRebateEnabled = useSelector(featureToggleSelectors.isKeepUnnamedManualRebateEnabled);
    // Cash has no lender program to identify, so Program # has no real value for cash - show the
    // literal "CASH" label instead of a blank field, rather than dropping the column.
    const showCashProgramLabel = offerType === CASH && isCashFixesEnabled;

    const [incentivesToEdit, setIncentivesToEdit] = useState<ManualIncentiveEditEntry[]>(() =>
        appliedIncentivesWithEditIds.map((inc) => ({ ...inc, _editId: inc._editId || generateEditId() }))
    );
    const [isFormValid, setFormValid] = useState(false);
    const [editedIncentiveIndex, setEditedIncentiveIndex] = useState(INCENTIVE_NOT_EDITED);
    const isLocked = dealLockHooks.useIsDealLocked();

    const incentivesToEditRef = useRef<ManualIncentiveEditEntry[]>(incentivesToEdit);
    useEffect(() => {
        incentivesToEditRef.current = incentivesToEdit;
    });
    const isEligibleForPaymentUpdate = useRef<boolean>(false);
    const hasEditIdSyncRun = useRef<boolean>(false);
    const hasEditIdStampRun = useRef<boolean>(false);
    const previousProgramContextId = useRef<string | undefined>(currentProgramContextId);

    useEffect(() => {
        if (!isRestorationEnabled || hasEditIdStampRun.current) {
            return;
        }
        const stored = (storedManualIncentives ?? []) as ManualIncentiveEditEntry[];
        if (!stored.length || stored.every((inc) => inc._editId)) {
            return;
        }
        hasEditIdStampRun.current = true;
        dispatch(
            offerActionCreators.updateManualIncentives({
                manualIncentives: stampMissingEditIds(stored, incentivesToEditRef.current, isKeepUnnamedManualRebateEnabled),
                dealerCashTotal,
                totalRebates: nonDealerCashTotal,
                skipPayment: true,
                preserveSelectedIncentives: true
            })
        );
    }, [
        storedManualIncentives,
        dealerCashTotal,
        nonDealerCashTotal,
        dispatch,
        isRestorationEnabled,
        isKeepUnnamedManualRebateEnabled
    ]);

    useEffect(() => {
        if (isRestorationEnabled) {
            return;
        }
        if (!hasEditIdSyncRun.current && incentivesToEdit.length > 0) {
            const active = incentivesToEdit.filter((inc) => !inc.deleted);
            dispatch(
                offerActionCreators.updateManualIncentives({
                    manualIncentives: active,
                    dealerCashTotal,
                    totalRebates: nonDealerCashTotal,
                    skipPayment: true,
                    preserveSelectedIncentives: true
                })
            );
            hasEditIdSyncRun.current = true;
        }
    }, [incentivesToEdit, dealerCashTotal, nonDealerCashTotal, dispatch, isRestorationEnabled]);

    useEffect(() => {
        if (isIncentivesFormDirtyRef) {
            const activeIncentivesToEdit = incentivesToEdit.filter((inc) => !inc.deleted);
            const activeSavedIncentives = appliedIncentivesWithEditIds.filter((inc) => !inc.deleted);

            const hasDataChanges = !isEqual(
                toComparableIncentives(activeIncentivesToEdit),
                toComparableIncentives(activeSavedIncentives)
            );

            isIncentivesFormDirtyRef.current = hasDataChanges || hasValidationErrors;
        }
    }, [incentivesToEdit, appliedIncentivesWithEditIds, hasValidationErrors, isIncentivesFormDirtyRef]);

    const setPaymentUpdateEligibility = useCallback(
        (shouldUpdate: boolean) => {
            isEligibleForPaymentUpdate.current = shouldUpdate;
            if (shouldUpdate && isFormValid) {
                dispatch(offerActionCreators.updatePaymentCallPendingState(true));
            }
        },
        [dispatch, isFormValid]
    );

    useEffect(() => {
        if (isApplying) return;
        setIncentivesToEdit((previousIncentivesToEdit) => {
            const hasProgramContextChanged =
                previousProgramContextId.current != null &&
                currentProgramContextId != null &&
                previousProgramContextId.current !== currentProgramContextId;

            previousProgramContextId.current = currentProgramContextId;

            if (hasProgramContextChanged) {
                return appliedIncentivesWithEditIds.map((incentive) => ({
                    ...incentive,
                    _editId: incentive._editId || generateEditId()
                }));
            }

            const merged = mergeIncentives(
                previousIncentivesToEdit,
                appliedIncentivesWithEditIds,
                isRestorationEnabled,
                isKeepUnnamedManualRebateEnabled
            );
            return isRestorationEnabled ? sortStandardIncentivesFirst(merged) : merged;
        });
    }, [appliedIncentivesWithEditIds, currentProgramContextId, isApplying, isKeepUnnamedManualRebateEnabled]);

    useEffect(() => {
        setFormValid(isIncentiveFormValid(incentivesToEdit, isOptionalDeskingMode));
    }, [incentivesToEdit, isOptionalDeskingMode]);

    const dispatchIncentiveModification = useCallback(
        (incentive: ManualIncentiveEditEntry, action: IncentiveModificationAction) => {
            const requiredFields = getRequiredFields(isOptionalDeskingMode);
            const hasRequiredFields = requiredFields.every((field) => incentive[field]);
            if (!hasRequiredFields && action !== INCENTIVE_MODIFICATION_ACTION.REMOVE) {
                return;
            }

            const toggleAction =
                action === INCENTIVE_MODIFICATION_ACTION.ADD
                    ? true
                    : action === INCENTIVE_MODIFICATION_ACTION.REMOVE
                    ? false
                    : null;
            const callPayment = hasRequiredFields && isEligibleForPaymentUpdate.current;
            const isDealerCash = isDealerCashEntry(incentive);
            const isProgramDealerCashStandard = isDealerCash && Boolean(incentive.isStandardIncentive) && !incentive.manual;
            const isFromProgramIncentives =
                isProgramDealerCashStandard ||
                (!isDealerCash && (isIncentiveFromProgram(incentive) || Boolean(incentive.isStandardIncentive)));
            const isStandardOverride = Boolean(incentive.isStandardIncentive) && Boolean(incentive.manual);
            const isOverride =
                isRestorationEnabled && !isDealerCash && (isProgramIncentiveOverride(incentive) || isStandardOverride);
            const asProgramIncentive = {
                ...incentive,
                incentiveId: incentive.code,
                programName: incentive.name,
                cash: incentive.amount
            };

            if (toggleAction !== null && isFromProgramIncentives && !isOverride) {
                dispatch(programActionCreators.toggleSelectedIncentiveCompactUI(asProgramIncentive, toggleAction, true));
            } else {
                dispatch(programActionCreators.modifyManualIncentiveCompactUI(incentive, action, callPayment));
            }
            isEligibleForPaymentUpdate.current = false;
        },
        [dispatch, isOptionalDeskingMode, isRestorationEnabled]
    );

    const handleAddIncentive = useCallback(() => {
        setIncentivesToEdit((prevIncentivesToEdit) => [...prevIncentivesToEdit, createNewManualIncentiveEntry()]);
    }, []);

    return (
        <RebatesEditWrapper>
            <ManualRebatesForm
                term={term}
                incentivesToEdit={incentivesToEdit}
                editedIncentiveIndex={editedIncentiveIndex}
                setPaymentUpdateEligibility={setPaymentUpdateEligibility}
                setEditedIncentiveIndex={setEditedIncentiveIndex}
                setIncentivesToEdit={setIncentivesToEdit}
                onIncentiveChange={dispatchIncentiveModification}
                showCashProgramLabel={showCashProgramLabel}
            />
            <AddIncentiveContainer>
                <Button
                    buttonStyle="secondary"
                    data-testid="add-incentives-button"
                    onClick={handleAddIncentive}
                    size="small"
                    disabled={isLocked || isApplying}
                >
                    {ADD_INCENTIVE_BUTTON}
                </Button>
            </AddIncentiveContainer>
        </RebatesEditWrapper>
    );
};
